import { SSE } from './sse.js'
import Deferred from 'p-defer'

const defaultRedirect = (address: string) => {
  window.location.href = address
}

type Spec = Record<string, any>
type BuildOptions = {
  call: Function
  stream: Function
  redirect?: Function
}
export function build(spec: Spec, { call, stream, redirect = defaultRedirect }: BuildOptions) {
  const controller: Record<string, any> = Alpine.reactive({})

  for (const [name, value] of Object.entries(spec)) {
    if (name.startsWith('$permission')) continue
    if (name.startsWith('$')) {
      const { returns, needs }: { returns: 'value' | 'generator'; needs: string[] } = value
      switch (returns) {
        case 'value':
          controller[name] = async function (...args: any[]) {
            const thisObj = Object.fromEntries(needs.map((propName) => [propName, this[propName]]))
            const response = await call.apply(this, [name, args, thisObj])
            if (response.status === 302) {
              redirect(response.location)
              return
            }

            if (response.delta) {
              Object.assign(this, response.delta)
            }

            if (response.error) {
              const err = new Error(response.error.message)
              // @ts-ignore
              err.details = response.error.details
              throw err
            }

            return response.result
          }
          break
        case 'generator':
          if (typeof stream !== 'function') throw new Error('stream is not a function')
          controller[name] = function (...args: any[]) {
            const thisObj = Object.fromEntries(needs.map((propName) => [propName, this[propName]]))

            const iter = stream(name, args, thisObj)

            const cancelled = Deferred<void>()

            return {
              [Symbol.asyncIterator]() {
                return this
              },

              async return() {
                cancelled.resolve()
                return iter.return()
              },

              async next() {
                const next = await Promise.race([iter.next(), cancelled.promise])
                if (!next) {
                  iter.return()
                  return { done: true }
                }

                const { done, value: response } = next

                if (done) {
                  return { done }
                }

                if (response.status === 302) {
                  redirect(response.location)
                  return { done: true }
                }

                if (response.delta) {
                  Object.assign(this, response.delta)
                }

                if (response.error) {
                  iter.return()
                  const err = new Error(response.error.message)
                  // @ts-ignore
                  err.details = response.error.details
                  throw err
                }

                if (response.yielded) {
                  return { done: false, value: response.yielded }
                }

                if ('result' in response) {
                  iter.return()
                  return {
                    done: true,
                    value: response.result
                  }
                } else {
                  return {
                    done: true
                  }
                }
              }
            }
          }
          break
        default:
          throw new Error('INVALID returns: ' + returns)
      }
    } else {
      controller[name] = value
    }
  }

  return controller
}

function defaultStreamer(url: string, payload: any) {
  const sse = new SSE(url, payload)

  let resolve: () => void = () => {}
  const queue: Record<string, any>[] = []
  let streaming = true

  const closed = Deferred<void>()

  // @ts-ignore
  sse.onmessage = (event) => {
    queue.push(JSON.parse(event.data))
    if (queue.length === 1) {
      resolve() // allow generator to resume
    }
  }

  sse.stream()

  return {
    [Symbol.asyncIterator]() {
      return this
    },
    async next() {
      if (!queue.length) {
        // nothign in queue, wait until push
        await Promise.race([new Promise<void>((r) => (resolve = r)), closed.promise])
      }

      const value = await Promise.race([queue.shift(), closed.promise])
      if (!value) {
        return {
          done: true
        }
      }

      return {
        value,
        done: false
      }
    },

    async return() {
      streaming = false
      closed.resolve()
      sse.close()
      return {
        done: true
      }
    }
  }
}

export async function connect<T extends Record<string, any> = Record<string, any>>(
  address: string,
  { fetcher = globalThis?.fetch, streamer = defaultStreamer, importer = (address: string) => import(address) } = {}
): Promise<T> {
  // add a random query param
  address = address.match(/[?]/) ? address.replace('?', `?_r=${randomInt()}&`) : address + `?_r=${randomInt()}`
  const spec = await importer(address).then((exported) => {
    if (exported.default) {
      return exported.default
    } else if (exported.status === 302) {
      window.location = exported.location
    }
    return {}
  })
  const call = (methodName: string, params: any[] = [], thisObj: Record<string, any>) =>
    fetcher(address, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      mode: 'cors',
      cache: 'no-cache',
      redirect: 'manual',
      body: JSON.stringify({
        _method: methodName,
        params,
        thisObj
      })
    }).then((res) => res.json())

  const stream = (methodName: string, params: any[] = [], thisObj: Record<string, any>) =>
    streamer(address, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      cache: 'no-cache',
      redirect: 'manual',
      payload: JSON.stringify({
        _method: methodName,
        params,
        thisObj
      })
    })

  return build(spec, { call, stream }) as T
}

function randomInt() {
  return Math.round(Math.random() * Number.MAX_SAFE_INTEGER)
}
